import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { SubmitHandler, useForm } from 'react-hook-form';
import { format } from 'date-fns';
import {
  selectPatientGuardians,
  updateGuardians,
} from '../../../redux/assessment';
import { useAppDispatch } from '../../../pages/assessment';
import { PatientGuardian, Precedence } from '../../../generated/graphql';
import { AssessmentButtons } from '../shared';
import { GuardianRelationLabelValueMap } from '../../../helpers/assessment';
import { GuardianDemographicsSecondary } from './guardian-demographics-secondary';
import { GuardianDemographicsPrimary } from './guardian-demographics-primary';

export interface GuardianFormValues {
  primaryGuardianFirstName: string;
  primaryGuardianLastName: string;
  primaryGuardianRelationToPatient: string;
  primaryGuardianBirthDate: string;
  primaryGuardianPhone: string;
  primaryGuardianEmail: string;

  isPrimaryGuardianAddressSameAsMinor: boolean;
  primaryGuardianAddressStreet1: string;
  primaryGuardianAddressStreet2: string;
  primaryGuardianAddressCity: string;
  primaryGuardianAddressState: string;
  primaryGuardianAddressZipCode: string;

  discoveryChannel: string;
  discoveryChannelOther: string;

  secondaryGuardianFirstName: string;
  secondaryGuardianLastName: string;
  secondaryGuardianRelationToPatient: string;
  secondaryGuardianBirthDate: string;
  secondaryGuardianPhone: string;
  secondaryGuardianEmail: string;

  isSecondaryGuardianAddressSameAsMinor: boolean;
  secondaryGuardianAddressStreet1: string;
  secondaryGuardianAddressStreet2: string;
  secondaryGuardianAddressCity: string;
  secondaryGuardianAddressState: string;
  secondaryGuardianAddressZipCode: string;
}

export const getRelationOptions = (moreGuardianRelationOptions: boolean) =>
  Object.keys(
    moreGuardianRelationOptions
      ? GuardianRelationLabelValueMap
      : {
          [`${
            GuardianRelationLabelValueMap[GuardianRelationLabelValueMap.Mother]
          }`]: GuardianRelationLabelValueMap.Mother,
          [`${
            GuardianRelationLabelValueMap[GuardianRelationLabelValueMap.Father]
          }`]: GuardianRelationLabelValueMap.Father,
          [`${
            GuardianRelationLabelValueMap[GuardianRelationLabelValueMap.Other]
          }`]: GuardianRelationLabelValueMap.Other,
        }
  ).map(label => ({ label, value: GuardianRelationLabelValueMap[label] }));

const GuardianDemographicsForm = () => {
  const dispatch = useAppDispatch();

  const patientGuardians = useSelector(selectPatientGuardians);
  const primaryGuardian = patientGuardians?.primary;
  const secondaryGuardian = patientGuardians?.secondary;

  const form = useForm<GuardianFormValues>({
    mode: 'onBlur',
    defaultValues: {
      isPrimaryGuardianAddressSameAsMinor:
        primaryGuardian?.isAddressLinkedWithPatient ?? false,
      isSecondaryGuardianAddressSameAsMinor:
        secondaryGuardian?.isAddressLinkedWithPatient ?? false,
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = form;

  const { addToast } = useToasts();

  const [hasSecondary, setHasSecondary] = useState<boolean>(
    patientGuardians?.secondary !== undefined
  );

  const onSubmit: SubmitHandler<GuardianFormValues> = async data => {
    try {
      const formattedBirthDate = (date: string) =>
        format(new Date(date), 'yyyy-MM-dd');
      const formattedPhone = (phone: string) => phone?.replace(/[^0-9]/g, '');
      const formattedDiscoveryChannel =
        data.discoveryChannel === 'Other' && data.discoveryChannelOther
          ? `Other: ${data.discoveryChannelOther}`
          : data.discoveryChannel;

      const primaryDataToSend: PatientGuardian = {
        precedence: Precedence.Primary,
        firstName: data.primaryGuardianFirstName,
        lastName: data.primaryGuardianLastName,
        email: data.primaryGuardianEmail,
        birthDate: formattedBirthDate(data.primaryGuardianBirthDate),
        phone: formattedPhone(data.primaryGuardianPhone),
        relationToPatient: data.primaryGuardianRelationToPatient,
        discoveryChannel: formattedDiscoveryChannel,
        isAddressLinkedWithPatient: data.isPrimaryGuardianAddressSameAsMinor,
        ...(!data.isPrimaryGuardianAddressSameAsMinor && {
          addressStreet1: data.primaryGuardianAddressStreet1,
          addressStreet2: data.primaryGuardianAddressStreet2,
          addressCity: data.primaryGuardianAddressCity,
          addressState: data.primaryGuardianAddressState,
          addressZipCode: data.primaryGuardianAddressZipCode,
        }),
      };

      const secondaryDataToSend: PatientGuardian = hasSecondary
        ? {
            precedence: Precedence.Secondary,
            firstName: data.secondaryGuardianFirstName,
            lastName: data.secondaryGuardianLastName,
            email: data.secondaryGuardianEmail,
            birthDate: formattedBirthDate(data.secondaryGuardianBirthDate),
            phone: formattedPhone(data.secondaryGuardianPhone),
            relationToPatient: data.secondaryGuardianRelationToPatient,
            isAddressLinkedWithPatient:
              data.isSecondaryGuardianAddressSameAsMinor,
            ...(!data.isSecondaryGuardianAddressSameAsMinor && {
              addressStreet1: data.secondaryGuardianAddressStreet1,
              addressStreet2: data.secondaryGuardianAddressStreet2,
              addressCity: data.secondaryGuardianAddressCity,
              addressState: data.secondaryGuardianAddressState,
              addressZipCode: data.secondaryGuardianAddressZipCode,
            }),
          }
        : undefined;

      await dispatch(
        updateGuardians({
          primary: primaryDataToSend,
          secondary: secondaryDataToSend,
        })
      );
    } catch (e) {
      addToast(
        'Sorry, something went wrong. Please refresh the page and try again.',
        { appearance: 'error' }
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <GuardianDemographicsPrimary
        form={form}
        primaryGuardian={primaryGuardian}
      />
      <GuardianDemographicsSecondary
        form={form}
        secondaryGuardian={secondaryGuardian}
        isExpanded={hasSecondary}
        setIsExpanded={setHasSecondary}
      />
      <AssessmentButtons
        primary={{
          children: 'Continue',
          type: 'submit',
          disabled: isSubmitting,
          loading: isSubmitting,
        }}
      />
    </form>
  );
};

export default GuardianDemographicsForm;
